import classes from './PostAnnouncementModal.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState, useRef, useCallback } from 'react'
import Toggle from '../../common/form/Toggle'
import { ReactComponent as PhotoIcon } from '../../../assets/images/common/icons/photo-icon.svg'
import { ReactComponent as Paperclip } from '../../../assets/images/common/icons/paperclip.svg'
import { ReactComponent as VideoIcon } from '../../../assets/images/common/icons/video-icon.svg'
import { ReactComponent as PlayVideoIcon } from '../../../assets/images/common/icons/play-video-icon.svg'
import { ReactComponent as Vector } from '../../../assets/images/common/icons/vector.svg'
import { ReactComponent as PlusSign } from '../../../assets/images/common/icons/plus-sign.svg'
import classNames from 'classnames'
import { toast } from 'react-toastify'
import { selectSelectedCoaches, selectSelectedPrograms, selectSelectedSeries, selectSelectedStudents } from '../../../reducers/programDashboardReducer'
import { requestEditActivityAnnouncement, requestPostActivityAnnouncement } from '../../../actions/activityActions'
import { addProgramAnnouncement, addCommunityAnnouncement, editProgramAnnouncement, editCommunityAnnouncement } from '../../../reducers/dashboardReducer'
import PropTypes from 'prop-types'
import axios from 'axios'
import { requestPresignedUrl } from '../../../actions/announcementsActions'
import Loader from '../../common/loader/legacy-loaders/Op36Loader-web'
import CoachHeaderInfo from '../../activity/CoachHeaderInfo'
import LoadingBar from 'react-top-loading-bar'
import Tiptap from '../../common/Tiptap'
import DocumentCard from './DocumentCard'
import { EMPTY_PARAGRAPH_MESSAGE, MAX_EMAIL_LIMIT, MB } from '../../../misc/Constants'

const PHOTO_SIZE_LIMIT = 2
const VIDEO_SIZE_LIMIT = 100
const DOCUMENT_SIZE_LIMIT = 18

//  TO DO : FIND WORKAROUND
/* eslint-disable max-lines-per-function */
// eslint-disable-next-line complexity
const PostAnnouncementForm = ({ onClose, isProgram, nextStep, programsText, isEdit, announcement, setWarningModal, setIsDirty }) => {
  const [message, setMessage] = useState('')
  const [title, setTitle] = useState('')
  const [sendAsEmail, setSendAsEmail] = useState(true)
  const [selectedAttachment, setSelectedAttachment] = useState(null)
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [attachmentPreview, setAttachmentPreview] = useState(null)
  const [videoUpload, setVideoUpload] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [removeAttachment, setRemoveAttachment] = useState(false)
  const dispatch = useDispatch()
  const selectedPrograms = useSelector(selectSelectedPrograms)
  const selectedSeries = useSelector(selectSelectedSeries)
  const selectedStudents = useSelector(selectSelectedStudents)
  const selectedCoaches = useSelector(selectSelectedCoaches)
  const user = useSelector(state => state.user.current_user)
  const [loading, setLoading] = useState(false)

  const displayVideoIcon = isEdit && announcement?.video?.attachment_url
  const displayPhotoIcon = isEdit && announcement?.photo?.original_file_url

  const totalParticipants =
    selectedPrograms?.reduce((acc, program) => acc + program?.students?.length, 0) +
    selectedSeries?.reduce((acc, series) => acc + series?.students?.length, 0) +
    selectedStudents?.length +
    selectedCoaches?.length

  useEffect(() => {
    if (isEdit) {
      setTitle(announcement.title)
      setMessage(announcement.caption)
      setAttachmentPreview(announcement?.photo?.original_file_url)
      setSelectedDocument(announcement?.document)
    }
  }, [isEdit, announcement])

  const handleRemoveImage = () => {
    setRemoveAttachment(true)
    setSelectedAttachment(null)
    setIsDirty(true)
  }

  const hiddenFileInput = useRef(null)
  const hiddenDocumentInput = useRef(null)

  const handleUploadClick = () => {
    hiddenFileInput.current.click()
  }

  const handleSelectDocument = () => {
    hiddenDocumentInput.current.click()
  }

  const onUploadImage = useCallback((event) => {

    setRemoveAttachment(false)
    const file = event.target.files[0]

    const maxSize = file?.type?.startsWith('image/') ? PHOTO_SIZE_LIMIT * MB : VIDEO_SIZE_LIMIT * MB

    if (file?.size > maxSize) {
      toast.error(
        file.type.startsWith('image/')
          ? `Photo must be smaller than ${PHOTO_SIZE_LIMIT} MB`
          : `Video must be smaller than ${VIDEO_SIZE_LIMIT} MB`,
        { position: toast.POSITION.TOP_RIGHT }
      )
      return
    }

    if (file) {
      setSelectedAttachment(file)
      const uploadPhotoPreview = URL.createObjectURL(file)
      setAttachmentPreview(uploadPhotoPreview)
      setIsDirty(true)
    }

  }, [setIsDirty])

  const onUploadAttachment = useCallback((event) => {
    const file = event.target.files[0]

    if (file?.size > DOCUMENT_SIZE_LIMIT * MB) {
      toast.error(`Document must be smaller than ${DOCUMENT_SIZE_LIMIT} MB`, { position: toast.POSITION.TOP_RIGHT })
      return
    }

    if (file) {
      setSelectedDocument(file)
      setIsDirty(true)
    }
  }, [setIsDirty])

  const submitEnabled = message.length > 0 && message !== EMPTY_PARAGRAPH_MESSAGE && (isEdit || isProgram || totalParticipants > 0)

  const onSubmit = async () => {
    setLoading(true)
    if (isEdit) {

      requestEditActivityAnnouncement(message, title, announcement.id).then((data) => {
        if (data.status === 201) {
          onClose()
          if (isProgram) { dispatch(editProgramAnnouncement(data.data.activity)) } else {
            dispatch(editCommunityAnnouncement(data.data.activity))
          }
          toast.success('Announcement edited successfully', { position: toast.POSITION.TOP_RIGHT })
          setIsDirty(false)
        } else { toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT }) }
        setLoading(false)
        setMessage('')
        setSelectedAttachment(null)
        setAttachmentPreview(null)
        setSendAsEmail(false)
      })
    } else {
      const selectedProgramIds = selectedPrograms.map(program => program?.id)
      const selectedSeriesIds = selectedSeries.map(series => series.id)
      const selectedStudentIds = selectedStudents.map(student => student.id)
      const selectedCoachesIds = selectedCoaches.map(coach => coach.id)
      const formData = new FormData()
      formData.append('activity[caption]', message)
      formData.append('activity[title]', title)
      const emailFlag = totalParticipants > MAX_EMAIL_LIMIT ? false : sendAsEmail
      formData.append('send_email', emailFlag)
      formData.append('remove_attachment', removeAttachment)
      selectedProgramIds.forEach((programId) => formData.append('course_ids[]', programId))
      selectedSeriesIds.forEach((seriesId) => formData.append('series_ids[]', seriesId))
      selectedStudentIds.forEach(id => formData.append('student_ids[]', id))
      selectedCoachesIds.forEach(id => formData.append('coach_ids[]', id))

      if (selectedDocument) { formData.append('attachment_document', selectedDocument) }

      if (selectedAttachment) {
        if (selectedAttachment.type.includes('video')) {
          const response = await requestPresignedUrl(selectedAttachment)
          const { data: { file_url, key } } = response
          setVideoUpload(true)
          setUploadProgress(0)
          await axios.put(file_url, selectedAttachment, {
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              setUploadProgress(percentCompleted)
            },
          })
          setVideoUpload(false)
          formData.append('attachment_key', key)
        } else {
          formData.append('attachment', selectedAttachment)
        }
      }

      requestPostActivityAnnouncement(formData).then((data) => {
        if (data.status === 201) {
          onClose()
          if (isProgram) { dispatch(addProgramAnnouncement(data.data.activity)) } else { dispatch(addCommunityAnnouncement(data.data.activity)) }
          toast.success('Announcement posted successfully', { position: toast.POSITION.TOP_RIGHT })
          setIsDirty(false)
        } else {
          toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
        }
        setLoading(false)
        setMessage('')
        setSelectedAttachment(null)
        setAttachmentPreview(null)
        setSendAsEmail(false)
      })
    }
  }

  return <>
    <CoachHeaderInfo coach={user} />
    {loading || videoUpload ? <div className={classes.loadingContainer}>
      <LoadingBar color={'#f63'} progress={uploadProgress} onLoaderFinished={() => setUploadProgress(0)}/>
      <Loader message={videoUpload ? 'UPLOADING VIDEO' : 'POSTING ANNOUNCEMENT'}/></div> :
      <div className={classes.bodyContainer}>
        <button className={classes.addProgramsButton} disabled={isProgram || isEdit} onClick={() => nextStep(true)}>
          <PlusSign/>
          <span>{programsText}</span>
        </button>
        <input
          type='text'
          placeholder='Message Title'
          value={title}
          onChange={(e) => {
            setTitle(e.target.value)
            setIsDirty(true)
          }}
          className={classes.input}
        />
        <Tiptap value={message} onChange={(val) => {
          setMessage(val)
          setIsDirty(true)
        }} />
        <div className={classes.mediaContainer}>
          <div className={classes.addMediaWrapper}>
            {selectedAttachment && <div className={classes.selectedImageContainer}>
              { selectedAttachment?.type?.includes('video') ?
                <div className={classes.selectedImage}><PlayVideoIcon className={classes.playVideo} /></div> :
                <img src={attachmentPreview} alt='Selected' className={classes.selectedImage} />
              }
              <button className={classes.removeImageButton} onClick={handleRemoveImage}>x</button>
            </div>}
            {displayVideoIcon && (!removeAttachment) && (!selectedAttachment) && <div className={classes.selectedImageContainer}>
              <div className={classes.selectedImage}><PlayVideoIcon className={classes.playVideo} />
              </div>
            </div>}

            {displayPhotoIcon && (!removeAttachment) && (!selectedAttachment) && <div className={classes.selectedImageContainer}>
              <div className={classes.selectedImage}><img src={attachmentPreview} alt='Selected' className={classes.selectedImage} />
              </div>
            </div>}
            {!isEdit && <button className={classNames(classes.addMediaContainer)} onClick={handleUploadClick}>
              <div className={classes.imagesContainer} hidden={selectedAttachment}>
                <PhotoIcon/>
                <VideoIcon/>
              </div>
              <span className={classes.addMediaText}>{`${selectedAttachment ? 'Media Uploaded' : 'Add media'}`}</span>
              <input className={classes.addMediaText} type='file' style={{ display: 'none' }} ref={hiddenFileInput}
                accept='image/*,video/*'
                onChange={onUploadImage} disabled={isEdit}/>
            </button>}
          </div>

          {selectedDocument && <DocumentCard
            name={selectedDocument.name}
            size={(selectedDocument.size / 1024).toFixed(1)}
            withRemove={!isEdit}
            onRemove={() => setSelectedDocument(null)} />}
        </div>
        <div className={classes.footerContainer}>
          <div className={classes.attachmentsContainer}>
            <button className={classes.paperclipContainer} onClick={handleSelectDocument}>
              <Paperclip />
              <span className={classes.toggleText}>Attach a File</span>
              <input type='file' style={{ display: 'none' }} ref={hiddenDocumentInput}
                onChange={onUploadAttachment} disabled={isEdit}
                accept='.pdf,.doc,.docx,.xls,.xlsx,.rtf'/>
            </button>
            <div className={classes.toggleSwitch}>
              <Toggle onChange={() => setSendAsEmail(!sendAsEmail)}
                defaultChecked={sendAsEmail} disabled={isEdit || totalParticipants > MAX_EMAIL_LIMIT} size={'small'}/>
              <span className={classes.toggleText}>Send as Email</span>
            </div>
          </div>
          <button className={classes.submitButton} disabled={!submitEnabled} onClick={onSubmit}>
            <span>{'Post'}</span>
            <div className={classNames(classes.vectorContainer, { [classes.vectorContainerDisabled]: !submitEnabled })}>
              <Vector/>
            </div>
          </button>
        </div>
        {totalParticipants > MAX_EMAIL_LIMIT && <span className={classes.limitReachedText}>
  You cannot send an email to more than 300 participants.{' '}
          <button className={classes.learnMore} onClick={() => setWarningModal(true)}>Learn More</button>
        </span>}
      </div>}</>
}

PostAnnouncementForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  programsText: PropTypes.string.isRequired,
  isProgram: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool,
  setWarningModal: PropTypes.func.isRequired,
  setIsDirty: PropTypes.func,
  announcement: PropTypes.shape({
    id: PropTypes.number.isRequired,
    caption: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    activity_date: PropTypes.string.isRequired,
    photo: PropTypes.shape({
      original_file_url: PropTypes.string,
    }),
    video: PropTypes.shape({
      attachment_url: PropTypes.string,
    }),
    courses: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
      })
    ),
    document: PropTypes.object,
    comment_count: PropTypes.number.isRequired,
    like_count: PropTypes.number,
    user: PropTypes.object.isRequired,
    removeActivity: PropTypes.func.isRequired,
  }),
}

export default PostAnnouncementForm
